import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@bi-studio/shared/hooks";
import { AppLayout, LoadingScreen, ErrorState } from "@bi-studio/shared/components";
import { useForm } from "@mantine/form";
import { updateTabGroup, getFolderForItem } from "@bi-studio/shared/apis";
import type { TabGroupInput } from "@bi-studio/shared/lib";
import { 
    PageContainer, 
    PageActions, 
    TabGroupEditActionsButton 
} from "@/components";
import { TabGroupEditView } from "@/components/tab-groups/TabGroupEditView";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { 
    fetchTabGroupDetails,
    fetchTabGroupSelectionLists,
    selectCurrentTabGroup,
    selectTabGroupReports,
    selectTabGroupCharts,
    selectTabGroupDashboards,
    selectTabGroupForms,
    selectTabGroupLoading,
    selectTabGroupListsLoading,
    selectTabGroupError,
    setTabGroupError,
    resetTabGroupState,
    clearCurrentTabGroup
} from "@/store/slices/tabGroupSlice";

export default function EditTabGroupPage() {
    const { isAuthenticated, isLoading: authLoading, token } = useAuth();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { id } = useParams<{ id: string }>();

    const [activeTab, setActiveTab] = React.useState<string | null>("general");
    const [isSaving, setIsSaving] = React.useState(false);
    
    const tabGroup = useAppSelector(selectCurrentTabGroup);
    const reports = useAppSelector(selectTabGroupReports);
    const charts = useAppSelector(selectTabGroupCharts);
    const dashboards = useAppSelector(selectTabGroupDashboards);
    const forms = useAppSelector(selectTabGroupForms);
    const isLoadingDetails = useAppSelector(selectTabGroupLoading);
    const isLoadingLists = useAppSelector(selectTabGroupListsLoading);
    const error = useAppSelector(selectTabGroupError);

    const loadingData = isLoadingDetails || isLoadingLists;

    const form = useForm<TabGroupInput>({
        initialValues: {
            name: "",
            description: "",
            allowedRoles: [],
            tabs: [],
        },
        validate: {
            name: (value) => (value ? null : "Name is required"),
        },
    });

    React.useEffect(() => {
        if (!authLoading && !isAuthenticated) {
            navigate("/login");
        }
    }, [isAuthenticated, authLoading, navigate]);

    React.useEffect(() => {
        if (token && id) {
            dispatch(fetchTabGroupDetails({ token, id }));
            dispatch(fetchTabGroupSelectionLists(token));
        }

        return () => {
            dispatch(clearCurrentTabGroup());
            dispatch(resetTabGroupState());
        };
    }, [dispatch, token, id]);

    // Populate form when tabGroup is loaded
    React.useEffect(() => {
        if (tabGroup) {
            form.setValues({
                name: tabGroup.name,
                description: tabGroup.description || "",
                allowedRoles: tabGroup.allowedRoles || [],
                tabs: tabGroup.tabs ? JSON.parse(JSON.stringify(tabGroup.tabs)) : [],
            });
        }
    }, [tabGroup]);

    const handleSave = async () => {
        const validation = form.validate();
        if (validation.hasErrors) return;
        
        if (!token || !id) return;

        for (const tab of form.values.tabs) {
            if (tab.type === "report" && !tab.reportId) {
                dispatch(setTabGroupError("Please select a report for all tabs"));
                setActiveTab("tabs");
                return;
            }
            if (tab.type === "chart" && !tab.chartId) {
                dispatch(setTabGroupError("Please select a chart for all tabs"));
                setActiveTab("tabs");
                return;
            }
            if (tab.type === "dashboard" && !tab.dashboardId) {
                dispatch(setTabGroupError("Please select a dashboard for all tabs"));
                setActiveTab("tabs");
                return;
            }
            if (tab.type === "form" && !tab.formId) {
                dispatch(setTabGroupError("Please select a form for all tabs"));
                setActiveTab("tabs");
                return;
            }
        }

        try {
            setIsSaving(true);
            dispatch(setTabGroupError(null));

            await updateTabGroup(token, id, form.values);
            navigate(`/tab-groups/${id}`);
        } catch (err) {
            dispatch(setTabGroupError(err instanceof Error ? err.message : "Failed to update tab group"));
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        navigate(`/tab-groups/${id}`);
    };

    const handleBack = async () => {
        if (!token || !id) {
            navigate(-1);
            return;
        }

        try {
            const folderInfo = await getFolderForItem(token, "tab_group", id);
            if (folderInfo.folderId) {
                navigate(`/folders/${folderInfo.folderId}`);
            } else {
                navigate("/folders");
            }
        } catch (err) {
            console.error("Failed to get folder for tab group:", err);
            navigate(-1);
        }
    };

    if (authLoading || (loadingData && !form.values.name)) {
        return (
            <AppLayout onLogout={() => navigate("/login")} title="Workspace" subtitle="BI Studio">
                <LoadingScreen message="Loading tab group configuration..." />
            </AppLayout>
        );
    }

    return (
        <AppLayout
            title="Workspace"
            subtitle="BI Studio"
            onLogout={() => navigate("/login")}
        >
            <PageContainer>
                <PageActions 
                    title={`Edit ${form.values.name || 'Tab Group'}`}
                    onBack={handleBack}
                >
                    <TabGroupEditActionsButton
                        handleCancel={handleCancel}
                        handleSave={handleSave}
                        isSaving={isSaving}
                    />
                </PageActions>

                {error && (
                    <ErrorState 
                        title="Failed to update tab group"
                        message={error || undefined}
                        onAction={() => dispatch(setTabGroupError(null))}
                    />
                )}

                <TabGroupEditView
                    isSaving={isSaving}
                    loadingData={loadingData}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    form={form}
                    reports={reports}
                    charts={charts}
                    dashboards={dashboards}
                    forms={forms}
                />
            </PageContainer>
        </AppLayout>
    );
}
